import { useAuth } from '../stores/supabaseAuthStore';

/**
 * Custom hook for user display information
 * Provides consistent user display logic across components
 */
export const useUserDisplay = () => {
  const { user } = useAuth();

  const getUserDisplayName = () => {
    if (user?.user_metadata?.name) {
      return user.user_metadata.name;
    }
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    if (name === 'User') return 'U';

    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const getAuthProvider = () => {
    const appMetadata = user?.app_metadata;
    if (appMetadata?.provider) {
      return appMetadata.provider.charAt(0).toUpperCase() + appMetadata.provider.slice(1);
    }
    return 'Email';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getUserEmail = () => {
    return user?.email || '';
  };

  const isEmailVerified = () => {
    return !!user?.email_confirmed_at;
  };

  const getMemberSinceDate = () => {
    return user?.created_at ? formatDate(user.created_at) : 'Unknown';
  };

  return {
    user,
    getUserDisplayName,
    getUserInitials,
    getAuthProvider,
    formatDate,
    getUserEmail,
    isEmailVerified,
    getMemberSinceDate,
  };
};
